/* ──────────────────────────────────────────────────────────────────────
 *  TeamMemberCard — Shared portrait card for team members
 *
 *  Used across Communities (Key Leaders), Projects (Team), and
 *  Chronicles (Team Credits).  Renders a gradient card with circular
 *  avatar, name, optional L4E-linked badge, external profile link,
 *  and role title pinned to the card bottom.
 *
 *  Size variants:
 *    'md' (default) — 120px avatar, compact card (Project / Chronicle)
 *    'lg'           — 150px avatar, taller card  (Community Leaders)
 *
 *  Design tokens used:
 *    --accent-bg / --accent-bg-deep   gradient background
 *    --text-on-accent                 text colour on gradient
 * ────────────────────────────────────────────────────────────────────── */

import React, { useState } from 'react';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { mediaSrc } from '../../api/mediaUrl';
import styles from './TeamMemberCard.module.css';

/* ── Props ────────────────────────────────────────────────────────── */

export interface TeamMemberCardProps {
  /** Display name shown below the avatar */
  displayName: string;
  /** URL for the member's photo (null/undefined = initial placeholder) */
  photoUrl?: string | null;
  /** Role or title shown pinned to card bottom */
  roleTitle?: string | null;
  /** When set, shows the green "L4E Member" badge */
  linkedUserGuid?: string | null;
  /** External profile URL — shows a link icon beside the badge row */
  externalProfileUrl?: string | null;
  /** True = dashed-border vacant placeholder card */
  isVacant?: boolean;
  /**
   * True = identity hidden (name replaced with placeholder, avatar suppressed).
   * Used by ProjectTeamRoster for visibility-restricted members.
   */
  isRedacted?: boolean;
  /**
   * Avatar variant for filled (non-vacant) slots.
   * 'core'     — brighter background (community core leaders)
   * 'expanded' — dimmer background (community expanded leaders)
   * 'l4e'      — blue ring; signals a verified Life4Earth platform user
   *              (Geo Community Leaders consumers pass this when a member's
   *              linked_user_guid is set). Distinguishes trusted-system
   *              users from external entries on the same surface.
   * 'default'  — standard placeholder background
   */
  avatarVariant?: 'default' | 'core' | 'expanded' | 'l4e';
  /**
   * When true, splits displayName into first/last on two lines.
   * Used by ProjectTeamRoster for the two-line name layout.
   */
  splitName?: boolean;
  /**
   * Card size variant.
   * 'md' (default) — 120px avatar, Project / Chronicle sizing
   * 'lg'           — 150px avatar, Community Leaders sizing
   */
  size?: 'md' | 'lg';
  /** Optional className for the outer card (for overrides) */
  className?: string;
}

/* ── Helpers ──────────────────────────────────────────────────────── */

/** Extract first initial from a display name */
function getInitial(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return '?';
  return trimmed.charAt(0).toUpperCase();
}

/* ---- Name-splitting logic ---- */

/** Common name prefixes (honorifics, titles) — lowercase for matching */
const NAME_PREFIXES = new Set([
  'mr.', 'mrs.', 'ms.', 'mx.', 'dr.', 'prof.', 'rev.', 'hon.',
  'sir', 'dame', 'sgt.', 'cpl.', 'capt.', 'maj.', 'col.', 'gen.', 'lt.',
]);

/** Common name suffixes — lowercase for matching */
const NAME_SUFFIXES = new Set([
  'jr.', 'jr', 'sr.', 'sr', 'ii', 'iii', 'iv', 'v', 'vi', 'vii', 'viii',
  'phd', 'ph.d.', 'md', 'm.d.', 'esq.', 'esq', 'cpa', 'dds', 'do', 'jd',
  'rn', 'dvm', 'pe', 'cfa',
]);

/**
 * Split a display name into two lines for the card layout.
 *
 * Line 1 (first line): prefixes + given/middle names
 * Line 2 (last line):  surname + suffixes
 *
 * Handles comma-delimited suffixes ("Davis, III") as well as
 * space-separated ones ("Smith Jr.").
 *
 * Returns { firstLine, lastLine }. If the name has only one
 * meaningful token, firstLine gets it and lastLine is empty.
 */
function splitDisplayName(name: string): { firstLine: string; lastLine: string } {
  const trimmed = name.trim();
  if (!trimmed) return { firstLine: '', lastLine: '' };

  // 1. Separate comma-delimited suffix (e.g. "Davis, III" → "Davis" + "III")
  let mainPart = trimmed;
  let commaSuffix = '';
  const commaIdx = trimmed.indexOf(',');
  if (commaIdx > 0) {
    mainPart = trimmed.slice(0, commaIdx).trim();
    commaSuffix = trimmed.slice(commaIdx + 1).trim();
  }

  const tokens = mainPart.split(/\s+/);

  // 2. Strip leading prefixes
  const prefixes: string[] = [];
  while (tokens.length > 1 && NAME_PREFIXES.has(tokens[0].toLowerCase())) {
    prefixes.push(tokens.shift()!);
  }

  // 3. Strip trailing suffixes (only from the non-comma portion)
  const suffixes: string[] = [];
  while (tokens.length > 1 && NAME_SUFFIXES.has(tokens[tokens.length - 1].toLowerCase())) {
    suffixes.unshift(tokens.pop()!);
  }

  // Add comma-separated suffix tokens
  if (commaSuffix) {
    commaSuffix.split(/\s+/).forEach((s) => suffixes.push(s));
  }

  // 4. Split remaining core: last token = surname, rest = given/middle
  if (tokens.length <= 1) {
    // Single core token (e.g. "Madonna" or "Dr. Smith")
    const firstLine = [...prefixes, tokens[0] || ''].join(' ');
    const lastLine = suffixes.length > 0 ? suffixes.join(' ') : '';
    return { firstLine, lastLine };
  }

  const surname = tokens.pop()!;
  const givenNames = [...prefixes, ...tokens].join(' ');
  const lastLine = suffixes.length > 0
    ? `${surname}, ${suffixes.join(' ')}`
    : surname;

  return { firstLine: givenNames, lastLine };
}

/* ── Component ────────────────────────────────────────────────────── */

export default function TeamMemberCard({
  displayName,
  photoUrl,
  roleTitle,
  linkedUserGuid,
  externalProfileUrl,
  isVacant = false,
  isRedacted = false,
  avatarVariant = 'default',
  splitName = false,
  size = 'md',
  className,
}: TeamMemberCardProps) {
  const { t } = useAppTranslation('common');
  const isLg = size === 'lg';
  const [failedUrl, setFailedUrl] = useState<string | null>(null);

  /* ---- Redacted overrides ---- */
  const effectiveName = isRedacted ? t('teamMemberCard.redacted') : displayName;
  const resolvedPhoto = mediaSrc(photoUrl);
  const effectivePhoto = isRedacted ? null : (resolvedPhoto && resolvedPhoto !== failedUrl ? resolvedPhoto : null);
  const effectiveLinked = isRedacted ? null : linkedUserGuid;

  /* ---- Avatar ---- */
  const renderAvatar = () => {
    const avatarSizeCls = isLg ? styles.avatarLg : '';
    const placeholderSizeCls = isLg ? styles.avatarPlaceholderLg : '';

    if (isVacant) {
      return (
        <div className={[styles.avatarPlaceholder, placeholderSizeCls, styles.avatarVacant].filter(Boolean).join(' ')}>
          {'\u2014'}
        </div>
      );
    }

    // Variant class is applied to both the photo img AND the initials
    // placeholder so a verified L4E member's blue ring shows up
    // regardless of whether they've uploaded a photo.
    const variantCls =
      avatarVariant === 'core' ? styles.avatarCore :
      avatarVariant === 'expanded' ? styles.avatarExpanded :
      avatarVariant === 'l4e' ? styles.avatarL4e :
      '';

    if (effectivePhoto) {
      return (
        <img
          className={[styles.avatar, avatarSizeCls, variantCls].filter(Boolean).join(' ')}
          src={effectivePhoto}
          alt={effectiveName}
          loading="lazy"
          onError={() => setFailedUrl(resolvedPhoto ?? null)}
        />
      );
    }

    return (
      <div className={[styles.avatarPlaceholder, placeholderSizeCls, variantCls].filter(Boolean).join(' ')}>
        {isRedacted ? '?' : getInitial(effectiveName)}
      </div>
    );
  };

  /* ---- Derived flags ---- */
  const hasLinkedBadge = !!effectiveLinked;

  /* ---- Name ---- */

  const renderName = () => {
    if (isVacant) {
      return (
        <span className={styles.vacantLabel}>
          {t('teamMemberCard.vacant')}
        </span>
      );
    }

    if (splitName && !isRedacted) {
      const { firstLine, lastLine } = splitDisplayName(effectiveName);
      return (
        <div className={styles.personNameBlock}>
          <span className={[styles.firstName, isLg ? styles.firstNameLg : ''].filter(Boolean).join(' ')}>{firstLine}</span>
          {lastLine && <span className={[styles.lastName, isLg ? styles.lastNameLg : ''].filter(Boolean).join(' ')}>{lastLine}</span>}
        </div>
      );
    }

    return (
      <span
        className={[styles.personName, isLg ? styles.personNameLg : ''].filter(Boolean).join(' ')}
        title={effectiveName}
      >
        {effectiveName}
      </span>
    );
  };

  /* ---- Badge row (L4E linked indicator only — external link moved to name) ---- */
  const renderBadges = () => {
    if (!hasLinkedBadge) return null;
    return (
      <div className={styles.badges}>
        <span className={styles.linkedBadge}>
          <span className={styles.linkedDot} />
          {t('teamMemberCard.l4eMember')}
        </span>
      </div>
    );
  };

  /* ---- Render ---- */
  const cardClasses = [
    styles.card,
    isLg ? styles.cardLg : '',
    className,
  ].filter(Boolean).join(' ');

  return (
    <div className={cardClasses}>
      {renderAvatar()}
      {renderName()}

      {roleTitle && (
        <span className={[styles.roleTitle, isLg ? styles.roleTitleLg : ''].filter(Boolean).join(' ')}>
          {roleTitle}
        </span>
      )}
    </div>
  );
}
